import { z } from "zod";
import { UUID_RE } from "./ids";
import { CurrencyCodeSchema, MinorAmount, SignedMinorAmount, QrProviderSchema } from "./money";
import { graphemeLength, HEADER_MAX_GRAPHEMES, LABEL_MAX_GRAPHEMES, REGISTER_NAME_MAX_GRAPHEMES } from "./text";

/**
 * The ledger (D-003). Every change to a register is an append-only event with a client-generated
 * UUIDv7 id. Balances, stock, and reports are derived by folding events (see ledger.ts).
 * These schemas are the contract between client, Register Durable Object, Queue consumer, D1, and backups.
 * Add fields or new event types freely; never rename or repurpose existing ones.
 */

export const Uuid = z.string().regex(UUID_RE, "must be a UUID");
export const Timestamp = z.number().int().positive();

const graphemeMax = (max: number) => ({
  check: (s: string) => graphemeLength(s) <= max,
  message: `at most ${max} characters`,
});
const boundedText = (max: number, hardMax = max * 8) =>
  z.string().trim().max(hardMax).refine(graphemeMax(max).check, { message: graphemeMax(max).message });

/** Curated tile/theme palette ids. The client maps these to colors in both themes (F-207, F-301). */
export const PALETTE_IDS = [
  "c01", "c02", "c03", "c04", "c05", "c06", "c07", "c08", "c09", "c10", "c11", "c12",
  // Lighter/pastel row, chosen to read well on the dark theme (D-041).
  "c13", "c14", "c15", "c16", "c17", "c18",
] as const;
export const PaletteId = z.enum(PALETTE_IDS);
export type PaletteId = z.infer<typeof PaletteId>;

// ---------- Products and modifiers ----------

export const ModifierSchema = z.object({
  id: Uuid,
  label: boundedText(24).refine((s) => s.length > 0, { message: "required" }),
  priceDeltaMinor: SignedMinorAmount,
  sortOrder: z.number().int().min(0),
});
export type Modifier = z.infer<typeof ModifierSchema>;

export const ModifierGroupSchema = z
  .object({
    id: Uuid,
    name: boundedText(24).refine((s) => s.length > 0, { message: "required" }),
    minSelect: z.number().int().min(0).max(30),
    maxSelect: z.number().int().min(1).max(30),
    sortOrder: z.number().int().min(0),
    modifiers: z.array(ModifierSchema).min(1).max(30),
  })
  .refine((g) => g.minSelect <= g.maxSelect, { message: "minSelect must not exceed maxSelect" });
export type ModifierGroup = z.infer<typeof ModifierGroupSchema>;

export const ProductSchema = z.object({
  id: Uuid,
  label: boundedText(LABEL_MAX_GRAPHEMES).refine((s) => s.length > 0, { message: "required" }),
  /** Tax-inclusive price in minor units (D-016, D-020). */
  priceMinor: MinorAmount,
  color: PaletteId,
  sortOrder: z.number().int().min(0),
  trackStock: z.boolean(),
  photoId: z.string().max(64).nullable().default(null),
  modifierGroups: z.array(ModifierGroupSchema).max(10).default([]),
  archivedAt: Timestamp.nullable().default(null),
});
export type Product = z.infer<typeof ProductSchema>;

// ---------- Register settings ----------

export const PayHandle = z.string().regex(/^[A-Za-z0-9_.-]{1,30}$/, "letters, digits, . _ - only");
export const PayHandlesSchema = z.object({
  cashapp: PayHandle.optional(),
  paypal: PayHandle.optional(),
  venmo: PayHandle.optional(),
});
export type PayHandles = z.infer<typeof PayHandlesSchema>;

export const ThemeSchema = z.object({
  accent: PaletteId.default("c01"),
  mode: z.enum(["system", "light", "dark"]).default("system"),
});
export type Theme = z.infer<typeof ThemeSchema>;

export const RegisterSettingsSchema = z.object({
  name: boundedText(REGISTER_NAME_MAX_GRAPHEMES).refine((s) => s.length > 0, { message: "required" }),
  headerText: boundedText(HEADER_MAX_GRAPHEMES).default(""),
  currency: CurrencyCodeSchema,
  theme: ThemeSchema.default({ accent: "c01", mode: "system" }),
  payHandles: PayHandlesSchema.default({}),
});
export type RegisterSettings = z.infer<typeof RegisterSettingsSchema>;
/**
 * A partial settings update. Built from field schemas WITHOUT the `.default()` wrappers rather than
 * `RegisterSettingsSchema.partial()`, because `.partial()` keeps the nested defaults: parsing a patch that
 * omits (say) `theme`/`payHandles` would still fill them in with defaults, and the ledger's shallow merge
 * would then reset those fields — e.g. saving pay handles reverted the theme to System and changing the
 * theme wiped saved handles (D-039). Keys the caller omits must stay absent.
 */
export const RegisterSettingsPatchSchema = z
  .object({
    name: RegisterSettingsSchema.shape.name,
    headerText: boundedText(HEADER_MAX_GRAPHEMES),
    currency: CurrencyCodeSchema,
    theme: ThemeSchema,
    payHandles: PayHandlesSchema,
  })
  .partial();
export type RegisterSettingsPatch = z.infer<typeof RegisterSettingsPatchSchema>;

// ---------- Sale line items and tenders ----------

export const LineModifierSchema = z.object({
  id: Uuid,
  groupId: Uuid,
  label: z.string().min(1).max(200),
  priceDeltaMinor: SignedMinorAmount,
});
export type LineModifier = z.infer<typeof LineModifierSchema>;

export const LineItemSchema = z.object({
  productId: Uuid,
  labelSnapshot: z.string().min(1).max(200),
  unitPriceMinor: MinorAmount,
  qty: z.number().int().min(1).max(999),
  modifiers: z.array(LineModifierSchema).max(20).default([]),
  lineTotalMinor: MinorAmount,
});
export type LineItem = z.infer<typeof LineItemSchema>;

export const SaleTenderSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("cash"), tenderedMinor: MinorAmount, changeMinor: MinorAmount }),
  z.object({ type: z.literal("qr"), provider: QrProviderSchema }),
]);
export type SaleTender = z.infer<typeof SaleTenderSchema>;

export const RefundTenderSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("cash") }),
  z.object({ type: z.literal("qr"), provider: QrProviderSchema }),
]);
export type RefundTender = z.infer<typeof RefundTenderSchema>;

export const CashReasonSchema = z.enum(["bank_drop", "change_run", "supplies", "other"]);
export type CashReason = z.infer<typeof CashReasonSchema>;
export const StockReasonSchema = z.enum(["restock", "adjust", "count", "damaged"]);
export type StockReason = z.infer<typeof StockReasonSchema>;

// ---------- Events ----------

const eventBase = {
  id: Uuid,
  v: z.literal(1),
  registerId: Uuid,
  deviceId: Uuid,
  /** Per-device monotonic sequence, for ordering events from one device and detecting gaps. */
  seq: z.number().int().min(0),
  /** Device clock, unix ms. Server order is assigned by the Register DO (protocol.ts). */
  occurredAt: Timestamp,
};

const ev = <T extends string, P extends z.ZodRawShape>(type: T, payload: P) =>
  z.object({ ...eventBase, type: z.literal(type), payload: z.object(payload) });

export const RegisterOpenedEvent = ev("register.opened", { dayId: Uuid, openingFloatMinor: MinorAmount });
export const RegisterClosedEvent = ev("register.closed", { dayId: Uuid, countedCashMinor: MinorAmount });
export const CashPaidInEvent = ev("cash.paid_in", { dayId: Uuid, amountMinor: MinorAmount, reason: CashReasonSchema });
export const CashPaidOutEvent = ev("cash.paid_out", { dayId: Uuid, amountMinor: MinorAmount, reason: CashReasonSchema });
export const SaleCompletedEvent = ev("sale.completed", {
  transactionId: Uuid,
  dayId: Uuid.nullable(),
  items: z.array(LineItemSchema).min(1).max(100),
  subtotalMinor: MinorAmount,
  totalMinor: MinorAmount,
  tender: SaleTenderSchema,
  customerId: Uuid.nullable().default(null),
  /**
   * Tip on top of the goods (D-063). `totalMinor` stays the goods total; the customer paid
   * `totalMinor + tipMinor`, so a cash tender's `tenderedMinor − changeMinor` equals that sum.
   */
  tipMinor: MinorAmount.default(0),
});
/** Undo within 60 s (F-407). A compensating event; the sale row stays with status 'voided'. */
export const SaleVoidedEvent = ev("sale.voided", { transactionId: Uuid });
export const RefundCompletedEvent = ev("refund.completed", {
  refundId: Uuid,
  dayId: Uuid.nullable(),
  /** null for a blind refund (F-502). */
  originalTransactionId: Uuid.nullable(),
  /** Items returned, for partial refunds and restocking. Empty for blind or amount-only refunds. */
  items: z.array(LineItemSchema).max(100).default([]),
  amountMinor: MinorAmount,
  tender: RefundTenderSchema,
  restock: z.boolean().default(true),
});
/**
 * Retroactive customer link/unlink for a past sale or refund (D-046). `sale.completed` already carries
 * `customerId` for the moment of sale; this event lets History correct it afterwards without rewriting the
 * immutable original event. `customerId` is nullable to support un-linking. Usable by cashiers (see
 * RegisterDO's #roleCheck) — not a config event, unlike product/register settings changes.
 */
export const SaleCustomerLinkedEvent = ev("sale.customerLinked", { transactionId: Uuid, customerId: Uuid.nullable() });
/**
 * The customer kept the change of a past cash sale (D-066): that change becomes the sale's tip and the drawer
 * expects it. `amountMinor` and `dayId` are copied from the sale (the Register DO checks they match), so the
 * D1 cash row never depends on the sale's row having landed first. Not a config event: cashiers may use it
 * on the open drawer day, like refunds and customer links.
 */
export const SaleChangeKeptEvent = ev("sale.changeKept", {
  transactionId: Uuid,
  dayId: Uuid.nullable(),
  amountMinor: MinorAmount.refine((n) => n > 0, { message: "amount must be positive" }),
});
export const StockAdjustedEvent = ev("stock.adjusted", {
  productId: Uuid,
  delta: z.number().int().min(-99_999).max(99_999).refine((d) => d !== 0, { message: "delta must be non-zero" }),
  reason: StockReasonSchema,
});
export const ProductUpsertedEvent = ev("product.upserted", { product: ProductSchema });
export const ProductArchivedEvent = ev("product.archived", { productId: Uuid });
/** Hard-delete a product. The UI only offers this for an archived product with no sales (F-305). */
export const ProductDeletedEvent = ev("product.deleted", { productId: Uuid });
export const RegisterUpdatedEvent = ev("register.updated", { patch: RegisterSettingsPatchSchema });

export const LedgerEventSchema = z.discriminatedUnion("type", [
  RegisterOpenedEvent,
  RegisterClosedEvent,
  CashPaidInEvent,
  CashPaidOutEvent,
  SaleCompletedEvent,
  SaleVoidedEvent,
  RefundCompletedEvent,
  SaleCustomerLinkedEvent,
  SaleChangeKeptEvent,
  StockAdjustedEvent,
  ProductUpsertedEvent,
  ProductArchivedEvent,
  ProductDeletedEvent,
  RegisterUpdatedEvent,
]);
export type LedgerEvent = z.infer<typeof LedgerEventSchema>;
export type EventType = LedgerEvent["type"];
export type EventOfType<T extends EventType> = Extract<LedgerEvent, { type: T }>;
/**
 * A payload as a caller writes it: fields with a schema default (`customerId`, `tipMinor`, a refund's
 * `items`/`restock`) may be left out, and `makeEvent` fills them in when it parses.
 */
export type EventPayloadInput<T extends EventType> = Extract<z.input<typeof LedgerEventSchema>, { type: T }>["payload"];

export const EVENT_TYPES = [
  "register.opened", "register.closed", "cash.paid_in", "cash.paid_out", "sale.completed", "sale.voided",
  "refund.completed", "sale.customerLinked", "sale.changeKept", "stock.adjusted", "product.upserted", "product.archived",
  "product.deleted", "register.updated",
] as const satisfies readonly EventType[];

/** Config events bump the register's configVersion; only Owner sessions may emit them (F-802). */
export const CONFIG_EVENT_TYPES = ["product.upserted", "product.archived", "product.deleted", "register.updated"] as const satisfies readonly EventType[];
export function isConfigEvent(e: LedgerEvent): boolean {
  return (CONFIG_EVENT_TYPES as readonly string[]).includes(e.type);
}

/** Sync state of an event on a device (D-012). Free and Pro use the same states (D-024). */
export const SYNC_STATES = ["local", "sent", "saved"] as const;
export type SyncState = (typeof SYNC_STATES)[number];

/** An event after the Register DO has accepted it. */
export const ServerEventSchema = z.intersection(
  LedgerEventSchema,
  z.object({ serverOrder: z.number().int().min(0), receivedAt: Timestamp }),
);
export type ServerEvent = LedgerEvent & { serverOrder: number; receivedAt: number };

export interface EventContext {
  registerId: string;
  deviceId: string;
  seq: number;
  occurredAt?: number;
  id?: string;
}

/** Build and validate an event. Throws ZodError on invalid payloads. */
export function makeEvent<T extends EventType>(
  type: T,
  payload: EventPayloadInput<T>,
  ctx: EventContext,
  newId: () => string,
): EventOfType<T> {
  const candidate = {
    id: ctx.id ?? newId(),
    v: 1 as const,
    registerId: ctx.registerId,
    deviceId: ctx.deviceId,
    seq: ctx.seq,
    occurredAt: ctx.occurredAt ?? Date.now(),
    type,
    payload,
  };
  return LedgerEventSchema.parse(candidate) as EventOfType<T>;
}
